import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Users, Upload as UploadIcon, Download, Filter, MapPin, Mail, Phone,
  AlertCircle, CheckCircle2, X, RefreshCw, Search, Tag, Pencil, Trash2, Save,
  Calendar, GraduationCap, MapPinned, IdCard, Circle, Send, UserPlus, MessageSquarePlus,
} from 'lucide-react';
import DropZone from '../components/DropZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/licensed-prospects`;
const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP'];
const STATUSES = ['Active', 'Permanent', 'Inactive', 'Expired', 'Suspended', 'Revoked', 'Delinquent'];

export default function ProspectsPage() {
  const [activeTab, setActiveTab] = useState('list');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stats`);
      setStats(data);
    } catch (_) {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Prospects</h1>
          <p className="text-muted-foreground mt-1">
            Licensed-therapist database for territory targeting, FB audiences, and recruiter outreach.
          </p>
        </div>
        {stats && (
          <div className="flex gap-4 text-sm">
            <Stat label="Total" value={stats.total} icon={Users} />
            <Stat label="With email" value={stats.with_email} icon={Mail} />
            <Stat label="With phone" value={stats.with_phone} icon={Phone} />
            {stats.pending_changes > 0 && (
              <Stat label="Pending review" value={stats.pending_changes} icon={AlertCircle} highlight />
            )}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list">Prospects</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">
            Review changes
            {stats?.pending_changes > 0 && (
              <Badge variant="destructive" className="ml-2">{stats.pending_changes}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <ProspectList stats={stats} onRefresh={fetchStats} />
        </TabsContent>
        <TabsContent value="import" className="mt-4">
          <ProspectImport
            onComplete={() => {
              fetchStats();
              setActiveTab('review');
            }}
          />
        </TabsContent>
        <TabsContent value="review" className="mt-4">
          <PendingChangesReview onRefresh={fetchStats} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${highlight ? 'bg-amber-50 text-amber-900' : 'bg-muted'}`}>
      <Icon size={16} />
      <div>
        <div className="text-xs text-muted-foreground leading-tight">{label}</div>
        <div className="font-semibold leading-tight">{(value ?? 0).toLocaleString()}</div>
      </div>
    </div>
  );
}

// =============================================================
// IMPORT
// =============================================================
function ProspectImport({ onComplete }) {
  const [file, setFile] = useState(null);
  const [optInBasis, setOptInBasis] = useState('public_record');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeText, setPurgeText] = useState('');
  const [purging, setPurging] = useState(false);

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await axios.post(`${API}/purge`, { confirm: 'DELETE ALL' });
      toast.success(
        `Deleted ${(data.deleted_prospects ?? 0).toLocaleString()} prospects and cleared the review queue. Upload a fresh file to re-add everyone with full info.`,
        { duration: 9000 },
      );
      setPurgeOpen(false);
      setPurgeText('');
      setResult(null);
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setPurging(false);
    }
  };

  const submit = async () => {
    if (!file) {
      toast.error('Pick a file first');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(
        `${API}/import?opt_in_basis=${optInBasis}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setResult(data);
      toast.success(
        `Import done — ${data.new_count} new, ${data.unchanged_count} unchanged, ${data.pending_changes_count} need review`
      );
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="font-heading text-lg font-semibold">Upload prospect list</h3>
        <p className="text-sm text-muted-foreground mt-1">
          CSV or Excel. The importer auto-detects common header names (License Number, First Name,
          Last Name, License Type, Status, Address, City, State, ZIP, Phone, Email, Expiration).
          Records with matching <code>license_number</code> are deduped — changed fields go to the
          <strong> Review changes</strong> tab.
        </p>
      </div>

      <DropZone
        file={file}
        onFile={(f) => { setFile(f); setResult(null); }}
        onClear={() => { setFile(null); setResult(null); }}
        accept=".csv,.txt,.xlsx,.xls,.xlsm,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        acceptExtensions={['csv', 'txt', 'xlsx', 'xls', 'xlsm']}
        label="Drag and drop a CSV or Excel file"
        hint="Up to ~100 MB. 50,000 rows imports in well under a minute."
        testId="prospect-import-upload"
        disabled={running}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Opt-in basis (how you obtained this list)</Label>
          <Select value={optInBasis} onValueChange={setOptInBasis} disabled={running}>
            <SelectTrigger className="mt-1" data-testid="opt-in-basis-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public_record">Public record (e.g. state license board)</SelectItem>
              <SelectItem value="explicit">Explicit consent (form opt-in)</SelectItem>
              <SelectItem value="inferred">Inferred / purchased / referral</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Stored on every prospect for audit / CAN-SPAM compliance.
          </p>
        </div>
        <div className="flex items-end">
          <Button
            onClick={submit}
            disabled={!file || running}
            className="w-full"
            data-testid="run-import-btn"
          >
            {running ? (
              <><RefreshCw size={16} className="mr-2 animate-spin" /> Importing…</>
            ) : (
              <><UploadIcon size={16} className="mr-2" /> Start import</>
            )}
          </Button>
        </div>
      </div>

      {result && (
        <Card className="p-4 bg-muted/30 space-y-2">
          <div className="flex items-center gap-2 font-medium">
            <CheckCircle2 size={18} className="text-green-600" /> Import complete
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <ResultStat label="Total rows" value={result.total_rows} />
            <ResultStat label="New" value={result.new_count} color="text-green-700" />
            <ResultStat label="Unchanged" value={result.unchanged_count} />
            <ResultStat label="Needs review" value={result.pending_changes_count} color="text-amber-700" />
            <ResultStat label="Skipped" value={result.skipped_rows} color="text-muted-foreground" />
            <ResultStat label="Errors" value={result.error_rows} color="text-muted-foreground" />
          </div>
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">Detected columns</summary>
            <pre className="mt-2 bg-background p-2 rounded overflow-x-auto">
              {JSON.stringify(result.detected_columns, null, 2)}
            </pre>
          </details>
        </Card>
      )}
    </Card>

      <Card className="p-6 border-red-200 bg-red-50/40 space-y-3" data-testid="prospects-danger-zone">
        <div className="flex items-start gap-2">
          <AlertCircle className="text-red-600 mt-0.5 shrink-0" size={18} />
          <div>
            <h3 className="font-heading text-lg font-semibold text-red-800">Start over — delete everything</h3>
            <p className="text-sm text-red-700/90 mt-1">
              Permanently removes <strong>all</strong> licensed prospects and the review queue so you can
              re-import a clean file with full license numbers and dates. This cannot be undone.
            </p>
          </div>
        </div>
        <AlertDialog open={purgeOpen} onOpenChange={(o) => { setPurgeOpen(o); if (!o) setPurgeText(''); }}>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100" data-testid="delete-all-open-btn">
              <Trash2 size={14} className="mr-1" /> Delete all prospects
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all prospects?</AlertDialogTitle>
              <AlertDialogDescription>
                This deletes every licensed prospect and clears the pending-changes queue.
                Type <strong>DELETE ALL</strong> below to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={purgeText}
              onChange={(e) => setPurgeText(e.target.value)}
              placeholder="DELETE ALL"
              data-testid="delete-all-confirm-input"
            />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-all-cancel-btn">Cancel</AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={purgeText.trim().toUpperCase() !== 'DELETE ALL' || purging}
                onClick={purge}
                data-testid="delete-all-confirm-btn"
              >
                {purging ? 'Deleting…' : 'Delete everything'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
}

function ResultStat({ label, value, color = 'text-foreground' }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${color}`}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

// =============================================================
// LIST + FILTERS + EXPORT
// =============================================================
function ProspectList({ stats, onRefresh }) {
  const [filters, setFilters] = useState({
    discipline: [],
    license_status: [],
    state: '',
    search: '',
    near_zip: '',
    radius_miles: 20,
    has_email: null,
    has_phone: null,
    contacted: null,
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    filters.discipline.forEach((d) => p.append('discipline', d));
    filters.license_status.forEach((s) => p.append('license_status', s));
    if (filters.state) p.append('state', filters.state);
    if (filters.search) p.append('search', filters.search);
    if (filters.near_zip) {
      p.append('near_zip', filters.near_zip);
      p.append('radius_miles', filters.radius_miles);
    }
    if (filters.has_email !== null) p.append('has_email', filters.has_email);
    if (filters.has_phone !== null) p.append('has_phone', filters.has_phone);
    if (filters.contacted !== null) p.append('contacted', filters.contacted);
    p.append('page', page);
    p.append('page_size', pageSize);
    return p;
  }, [filters, page, pageSize]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/?${buildParams().toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load prospects');
      setData({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const toggleDiscipline = (d) => {
    setPage(1);
    setFilters((f) => ({
      ...f,
      discipline: f.discipline.includes(d)
        ? f.discipline.filter((x) => x !== d)
        : [...f.discipline, d],
    }));
  };

  const toggleStatus = (s) => {
    setPage(1);
    setFilters((f) => ({
      ...f,
      license_status: f.license_status.includes(s)
        ? f.license_status.filter((x) => x !== s)
        : [...f.license_status, s],
    }));
  };

  const exportFile = async (format) => {
    setExporting(true);
    try {
      const body = {
        format,
        discipline: filters.discipline.length ? filters.discipline : null,
        license_status: filters.license_status.length ? filters.license_status : null,
        state: filters.state || null,
        search: filters.search || null,
        has_email: filters.has_email,
        has_phone: filters.has_phone,
        near_zip: filters.near_zip || null,
        radius_miles: filters.radius_miles,
      };
      const res = await axios.post(`${API}/export`, body, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      const suffix = format === 'facebook' ? 'fb-audience'
        : format === 'mailchimp' ? 'mailchimp'
        : 'prospects';
      a.download = `${suffix}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.total.toLocaleString()} prospect${data.total === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Filter size={16} /> Filters
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Discipline</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {DISCIPLINES.map((d) => (
                <Chip
                  key={d}
                  active={filters.discipline.includes(d)}
                  onClick={() => toggleDiscipline(d)}
                  testId={`filter-discipline-${d}`}
                >
                  {d}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">License status</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {STATUSES.map((s) => (
                <Chip
                  key={s}
                  active={filters.license_status.includes(s)}
                  onClick={() => toggleStatus(s)}
                  testId={`filter-status-${s}`}
                >
                  {s}
                </Chip>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Near ZIP</Label>
            <Input
              value={filters.near_zip}
              placeholder="e.g. 78701"
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, near_zip: e.target.value.replace(/\D/g, '').slice(0, 5) }));
              }}
              data-testid="filter-near-zip"
            />
          </div>
          <div>
            <Label className="text-xs">Within (miles)</Label>
            <Input
              type="number"
              min={1}
              max={500}
              value={filters.radius_miles}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, radius_miles: parseInt(e.target.value, 10) || 20 }));
              }}
              disabled={!filters.near_zip}
              data-testid="filter-radius"
            />
          </div>
          <div>
            <Label className="text-xs">State</Label>
            <Input
              value={filters.state}
              maxLength={2}
              placeholder="TX"
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, state: e.target.value.toUpperCase() }));
              }}
              data-testid="filter-state"
            />
          </div>
          <div>
            <Label className="text-xs">Search name / city / email / phone / license</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-7"
                value={filters.search}
                onChange={(e) => {
                  setPage(1);
                  setFilters((f) => ({ ...f, search: e.target.value }));
                }}
                placeholder="Jane Doe, Houston, 512-555…, jdoe@…"
                data-testid="filter-search"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Chip
            active={filters.has_email === true}
            onClick={() => setFilters((f) => ({ ...f, has_email: f.has_email === true ? null : true }))}
            testId="filter-has-email"
          >
            <Mail size={12} className="mr-1 inline" /> Has email
          </Chip>
          <Chip
            active={filters.has_phone === true}
            onClick={() => setFilters((f) => ({ ...f, has_phone: f.has_phone === true ? null : true }))}
            testId="filter-has-phone"
          >
            <Phone size={12} className="mr-1 inline" /> Has phone
          </Chip>
          <Chip
            active={filters.contacted === false}
            onClick={() => setFilters((f) => ({ ...f, contacted: f.contacted === false ? null : false }))}
            testId="filter-not-contacted"
          >
            <Circle size={12} className="mr-1 inline" /> Not contacted
          </Chip>
          <Chip
            active={filters.contacted === true}
            onClick={() => setFilters((f) => ({ ...f, contacted: f.contacted === true ? null : true }))}
            testId="filter-contacted"
          >
            <CheckCircle2 size={12} className="mr-1 inline" /> Contacted
          </Chip>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={fetchList} disabled={loading} data-testid="refresh-list">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </Card>

      {/* Result count + Export */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {loading ? 'Loading…' : (
            <>
              <span className="font-semibold text-foreground">{data.total.toLocaleString()}</span>{' '}
              prospect{data.total === 1 ? '' : 's'} match the current filters
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => exportFile('csv')} disabled={exporting || !data.total} data-testid="export-csv">
            <Download size={14} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile('excel')} disabled={exporting || !data.total} data-testid="export-excel">
            <Download size={14} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile('facebook')} disabled={exporting || !data.total} data-testid="export-fb">
            <Download size={14} className="mr-1" /> Facebook audience
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile('mailchimp')} disabled={exporting || !data.total} data-testid="export-mailchimp">
            <Download size={14} className="mr-1" /> Mailchimp-ready
          </Button>
        </div>
      </div>

      {/* List */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Disc.</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">License #</th>
                <th className="px-3 py-2 text-left">Expires</th>
                <th className="px-3 py-2 text-left">City / ZIP</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Phone</th>
                {filters.near_zip && <th className="px-3 py-2 text-right">Distance</th>}
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr
                  key={p.id}
                  className="border-t hover:bg-primary/5 cursor-pointer"
                  onClick={() => setDetail(p)}
                  data-testid={`prospect-row-${p.id}`}
                >
                  <td className="px-3 py-2 font-medium text-primary hover:underline">
                    <div className="flex items-center gap-2">
                      {p.contacted && (
                        <span
                          title={`Contacted${p.contacted_at ? ' · ' + p.contacted_at.slice(0, 10) : ''}`}
                          className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 shrink-0"
                          data-testid={`contacted-dot-${p.id}`}
                        />
                      )}
                      <span>{p.first_name} {p.last_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2"><Badge variant="outline">{p.discipline || '—'}</Badge></td>
                  <td className="px-3 py-2">
                    <StatusBadge status={p.license_status} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{p.license_number || '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground tabular-nums">{p.license_expires || '—'}</td>
                  <td className="px-3 py-2">
                    {p.city ? <>{p.city}{p.state ? `, ${p.state}` : ''} {p.zip_code || ''}</> : '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">
                    {p.email || '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{p.phone || '—'}</td>
                  {filters.near_zip && (
                    <td className="px-3 py-2 text-right tabular-nums">
                      {p.distance_miles != null ? `${p.distance_miles} mi` : '—'}
                    </td>
                  )}
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={filters.near_zip ? 9 : 8} className="px-3 py-12 text-center text-muted-foreground">
                    No prospects match these filters yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20 text-sm">
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              ← Previous
            </Button>
            <span className="text-muted-foreground">Page {page} of {totalPages.toLocaleString()}</span>
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              Next →
            </Button>
          </div>
        )}
      </Card>

      <ProspectDetailModal
        prospect={detail}
        onClose={() => setDetail(null)}
        onSaved={(updated) => {
          setDetail(updated);
          fetchList();
        }}
        onDeleted={() => {
          setDetail(null);
          fetchList();
          onRefresh?.();
        }}
      />
    </div>
  );
}

function Chip({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 rounded-full text-xs border transition ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-background hover:bg-muted border-muted-foreground/20 text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  if (!status) return <Badge variant="outline">—</Badge>;
  const colors = {
    Active: 'bg-green-100 text-green-800 border-green-300',
    Permanent: 'bg-blue-100 text-blue-800 border-blue-300',
    Inactive: 'bg-gray-100 text-gray-700 border-gray-300',
    Expired: 'bg-red-100 text-red-700 border-red-300',
    Suspended: 'bg-amber-100 text-amber-800 border-amber-300',
    Revoked: 'bg-red-100 text-red-700 border-red-300',
    Delinquent: 'bg-amber-100 text-amber-800 border-amber-300',
  };
  const cls = colors[status] || 'bg-muted text-foreground';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${cls}`}>{status}</span>;
}

// =============================================================
// PROSPECT DETAIL / EDIT MODAL
// =============================================================
function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon size={15} className="mt-0.5 text-muted-foreground shrink-0" />}
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value || <span className="text-muted-foreground">—</span>}</div>
      </div>
    </div>
  );
}

function ProspectDetailModal({ prospect, onClose, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [rec, setRec] = useState(prospect);
  const [noteText, setNoteText] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    if (prospect) {
      setEditing(false);
      setRec(prospect);
      setNoteText('');
      setForm({
        first_name: prospect.first_name || '',
        last_name: prospect.last_name || '',
        email: prospect.email || '',
        phone: prospect.phone || '',
        street: prospect.street || '',
        city: prospect.city || '',
        state: prospect.state || '',
        zip_code: prospect.zip_code || '',
        discipline: prospect.discipline || '',
        license_status: prospect.license_status || '',
        notes: prospect.notes || '',
      });
    }
  }, [prospect]);

  if (!prospect) return null;
  const p = rec || prospect;
  const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Prospect';
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const patchRec = (patch) => {
    const next = { ...p, ...patch };
    setRec(next);
    onSaved?.(next);
  };

  const toggleContact = async () => {
    setBusy(true);
    try {
      const next = !p.contacted;
      const { data } = await axios.post(`${API}/${p.id}/contact`, { contacted: next });
      patchRec({ contacted: data.contacted, contacted_at: data.contacted_at });
      toast.success(next ? 'Marked as contacted' : 'Marked as not contacted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/${p.id}/notes`, { content: noteText.trim() });
      patchRec({ notes_log: [...(p.notes_log || []), data] });
      setNoteText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setBusy(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/${p.id}/notes/${noteId}`);
      patchRec({ notes_log: (p.notes_log || []).filter((n) => n.id !== noteId) });
    } catch (e) {
      toast.error('Failed to delete note');
    }
  };

  const promote = async () => {
    if (!window.confirm(`Promote ${fullName} to an ATS applicant? All their info and notes will carry over.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/${p.id}/promote`);
      patchRec({ promoted_to_applicant_id: data.applicant_id });
      toast.success(data.already_promoted ? 'Already an applicant' : 'Promoted to applicant', {
        description: 'Find them in Personnel → Applicants.',
        duration: 7000,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Promote failed');
    } finally {
      setBusy(false);
    }
  };

  const onEmailSent = (subject) => {
    const note = { id: `local-${Date.now()}`, content: `Emailed: ${subject}`, author: 'You', created_at: new Date().toISOString() };
    patchRec({
      contacted: true,
      contacted_at: new Date().toISOString(),
      notes_log: [...(p.notes_log || []), note],
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/${p.id}`, form);
      if (data.updated === false) {
        toast.info('No changes to save');
      } else {
        toast.success('Prospect updated');
      }
      onSaved?.({ ...p, ...form });
      setRec({ ...p, ...form });
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${fullName} from prospects? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/${p.id}`);
      toast.success('Prospect deleted');
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span data-testid="prospect-detail-name">{fullName}</span>
            {p.discipline && <Badge variant="outline">{p.discipline}</Badge>}
            <StatusBadge status={p.license_status} />
            {p.contacted && (
              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 border border-green-300 px-2 py-0.5 rounded-full" data-testid="detail-contacted-badge">
                <CheckCircle2 size={11} /> Contacted{p.contacted_at ? ` · ${p.contacted_at.slice(0, 10)}` : ''}
              </span>
            )}
            {p.promoted_to_applicant_id && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-100 border border-blue-300 px-2 py-0.5 rounded-full">
                <UserPlus size={11} /> Applicant
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {[p.discipline, p.city && `${p.city}${p.state ? ', ' + p.state : ''}`, p.email]
              .filter(Boolean).join(' · ') || 'Licensed prospect details'}
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <div className="flex flex-wrap gap-2 pb-1 border-b">
            <Button
              size="sm"
              variant={p.contacted ? 'secondary' : 'default'}
              onClick={toggleContact}
              disabled={busy}
              data-testid="contact-toggle-btn"
            >
              {p.contacted
                ? (<><CheckCircle2 size={14} className="mr-1" /> Contacted</>)
                : (<><Circle size={14} className="mr-1" /> Mark contacted</>)}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEmailOpen(true)}
              disabled={busy || !p.email}
              title={p.email ? '' : 'No email address on file'}
              data-testid="email-prospect-btn"
            >
              <Send size={14} className="mr-1" /> Email
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={promote}
              disabled={busy || !!p.promoted_to_applicant_id}
              data-testid="promote-applicant-btn"
            >
              <UserPlus size={14} className="mr-1" />
              {p.promoted_to_applicant_id ? 'Promoted' : 'Promote to applicant'}
            </Button>
          </div>
        )}

        {!editing ? (
          <div className="space-y-5">
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Contact</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={Mail} label="Email" value={p.email} />
                <Field icon={Phone} label="Phone" value={p.phone} />
              </div>
            </section>
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">License</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={IdCard} label="License #" value={p.license_number} />
                <Field icon={CheckCircle2} label="Status" value={p.license_status} />
                <Field icon={Calendar} label="Issued" value={p.license_issued} />
                <Field icon={Calendar} label="Expires" value={p.license_expires} />
              </div>
            </section>
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Location</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={MapPinned} label="Street" value={p.street} />
                <Field icon={MapPin} label="City / State / ZIP" value={[p.city, p.state, p.zip_code].filter(Boolean).join(', ')} />
                <Field icon={GraduationCap} label="School" value={p.school} />
              </div>
            </section>
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Record</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Opt-in basis" value={p.opt_in_basis} />
                <Field label="Source" value={p.opt_in_source || p.source_file} />
                <Field label="First imported" value={(p.first_imported_at || '').slice(0, 10)} />
                <Field label="Last seen" value={(p.last_seen_at || '').slice(0, 10)} />
              </div>
              {p.notes && <div className="mt-3"><Field label="Notes" value={p.notes} /></div>}
              {Array.isArray(p.tags) && p.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.tags.map((t) => <Badge key={t} variant="secondary" className="text-xs"><Tag size={10} className="mr-1" />{t}</Badge>)}
                </div>
              )}
            </section>

            <section data-testid="prospect-notes-section">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                <MessageSquarePlus size={13} /> Notes / Activity
              </h4>
              <div className="flex gap-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={2}
                  placeholder="Add a note (call summary, follow-up, etc.)"
                  data-testid="note-input"
                />
                <Button onClick={addNote} disabled={busy || !noteText.trim()} data-testid="add-note-btn" className="self-end">
                  Add
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {(p.notes_log || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No notes yet.</p>
                )}
                {[...(p.notes_log || [])].reverse().map((n) => (
                  <div key={n.id} className="group rounded-md border bg-muted/20 p-2 text-sm" data-testid={`note-item-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="whitespace-pre-wrap break-words">{n.content}</div>
                      {!String(n.id).startsWith('local-') && (
                        <button
                          onClick={() => deleteNote(n.id)}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 shrink-0"
                          title="Delete note"
                          data-testid={`delete-note-${n.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {n.author || 'Staff'} · {(n.created_at || '').slice(0, 16).replace('T', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">First name</Label><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="edit-first-name" /></div>
              <div><Label className="text-xs">Last name</Label><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="edit-last-name" /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="edit-email" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="edit-phone" /></div>
              <div>
                <Label className="text-xs">Discipline</Label>
                <Select value={form.discipline || undefined} onValueChange={(v) => set('discipline', v)}>
                  <SelectTrigger data-testid="edit-discipline"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">License status</Label>
                <Select value={form.license_status || undefined} onValueChange={(v) => set('license_status', v)}>
                  <SelectTrigger data-testid="edit-status"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Street</Label><Input value={form.street} onChange={(e) => set('street', e.target.value)} data-testid="edit-street" /></div>
              <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="edit-city" /></div>
              <div><Label className="text-xs">State</Label><Input value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))} data-testid="edit-state" /></div>
              <div><Label className="text-xs">ZIP</Label><Input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} data-testid="edit-zip" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} data-testid="edit-notes" /></div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {!editing ? (
            <>
              <Button variant="outline" onClick={remove} disabled={saving} className="text-red-600 hover:text-red-700" data-testid="delete-prospect-btn">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={onClose} data-testid="close-detail-btn">Close</Button>
              <Button onClick={() => setEditing(true)} data-testid="edit-prospect-btn">
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving} data-testid="cancel-edit-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-prospect-btn">
                <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      <EmailComposeDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        prospect={p}
        onSent={onEmailSent}
      />
    </Dialog>
  );
}

// =============================================================
// EMAIL COMPOSE (from prospect profile) — template picker + send
// =============================================================
function EmailComposeDialog({ open, onClose, prospect, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject('');
      setBody('');
      axios.get(`${API.replace('/licensed-prospects', '')}/email-templates`)
        .then(({ data }) => setTemplates(data || []))
        .catch(() => setTemplates([]));
    }
  }, [open]);

  const fillName = (text) => (text || '')
    .replace(/\{\{first_name\}\}/g, prospect?.first_name || '')
    .replace(/\{\{last_name\}\}/g, prospect?.last_name || '');

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(fillName(t.subject));
    // Strip tags for the editable plain body; server sends as HTML.
    const div = document.createElement('div');
    div.innerHTML = fillName(t.body_html || '');
    setBody(div.textContent || div.innerText || '');
  };

  const send = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setSending(true);
    try {
      const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#0f172a;white-space:pre-wrap;">${body.replace(/</g, '&lt;')}</div>`;
      await axios.post(`${API}/${prospect.id}/email`, { subject: subject.trim(), body_html: html });
      toast.success(`Email sent to ${prospect.email}`);
      onSent?.(subject.trim());
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed — is a Google mailbox connected?');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="email-compose-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send size={16} /> Email {prospect?.first_name} {prospect?.last_name}</DialogTitle>
          <DialogDescription>Sends from your connected Google mailbox and marks them contacted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">To</Label>
            <Input value={prospect?.email || ''} disabled data-testid="email-to" />
          </div>
          {templates.length > 0 && (
            <div>
              <Label className="text-xs">Template (optional)</Label>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger data-testid="email-template-select"><SelectValue placeholder="Start from a template…" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid="email-body" />
            <p className="text-xs text-muted-foreground mt-1">Your saved signature &amp; logo are added automatically.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="email-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending || !prospect?.email} data-testid="email-send-btn">
            <Send size={14} className="mr-1" /> {sending ? 'Sending…' : 'Send email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================
// PENDING CHANGES REVIEW
// =============================================================
function PendingChangesReview({ onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pending-changes?status=pending&limit=500`);
      setItems(data.items || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load changes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const act = async (action) => {
    if (selected.size === 0) {
      toast.error('Select at least one change');
      return;
    }
    setWorking(true);
    try {
      const { data } = await axios.post(`${API}/pending-changes/${action}`, {
        change_ids: Array.from(selected),
      });
      toast.success(`${action === 'accept' ? 'Applied' : 'Rejected'} ${data.applied ?? data.rejected ?? 0} change(s)`);
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setWorking(false);
    }
  };

  const repair = async () => {
    if (!window.confirm(
      'Rebuild every prospect from the pending queue?\n\n'
      + 'This creates a unique record for each pending row (merging true duplicates) '
      + 'and marks them accepted. Use this to recover records lost to the old '
      + 'import collision. It is safe to run more than once.'
    )) return;
    setRepairing(true);
    try {
      const { data } = await axios.post(`${API}/repair`);
      toast.success(
        `Rebuilt — ${data.prospects_created} created, ${data.prospects_updated} updated. `
        + `${(data.total_prospects_now ?? 0).toLocaleString()} total prospects now.`,
        { duration: 8000 },
      );
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const RepairBanner = (
    <Card className="p-4 border-amber-300 bg-amber-50" data-testid="repair-banner">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <AlertCircle size={20} className="text-amber-600 shrink-0" />
        <div className="flex-1 text-sm text-amber-900">
          <div className="font-medium">Recover imported prospects</div>
          <div className="text-amber-800/90">
            Rebuilds a unique record for every pending row (fixes the old import
            collision where many people overwrote the same record). Safe to re-run.
          </div>
        </div>
        <Button
          size="sm"
          onClick={repair}
          disabled={repairing || working}
          data-testid="repair-prospects-btn"
        >
          <RefreshCw size={14} className={`mr-1 ${repairing ? 'animate-spin' : ''}`} />
          {repairing ? 'Rebuilding…' : 'Rebuild from pending'}
        </Button>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {RepairBanner}
        <Card className="p-12 text-center text-muted-foreground">Loading…</Card>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="space-y-3">
        {RepairBanner}
        <Card className="p-12 text-center text-muted-foreground space-y-2">
          <CheckCircle2 size={36} className="mx-auto text-green-600" />
          <div>No pending changes. Re-import a file to see suggested updates here.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {RepairBanner}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-changes">
            {selected.size === items.length ? 'Deselect all' : 'Select all'} ({items.length})
          </Button>
          <Button size="sm" onClick={() => act('accept')} disabled={working || !selected.size} data-testid="accept-changes-btn">
            <CheckCircle2 size={14} className="mr-1" /> Accept ({selected.size})
          </Button>
          <Button variant="outline" size="sm" onClick={() => act('reject')} disabled={working || !selected.size} data-testid="reject-changes-btn">
            <X size={14} className="mr-1" /> Reject ({selected.size})
          </Button>
          <div className="flex-1" />
          <span className="text-sm text-muted-foreground">
            {selected.size} of {items.length} selected
          </span>
        </div>

        <div className="space-y-3">
          {items.map((c) => (
            <ChangeRow
              key={c.id}
              change={c}
              checked={selected.has(c.id)}
              onToggle={() => toggle(c.id)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

function ChangeRow({ change, checked, onToggle }) {
  const fields = Object.entries(change.diff || {});
  return (
    <div className={`border rounded-lg p-3 ${checked ? 'border-primary bg-primary/5' : 'border-muted-foreground/15'}`}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-1"
          data-testid={`change-checkbox-${change.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="font-medium">
              {change.first_name} {change.last_name}
            </div>
            <Badge variant="outline" className="text-xs">{change.license_number || '—'}</Badge>
            <span className="text-xs text-muted-foreground">from {change.source_file}</span>
          </div>
          <table className="text-xs w-full">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left w-28 pb-1">Field</th>
                <th className="text-left pb-1">Current</th>
                <th className="text-left pb-1">New (from file)</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(([f, vals]) => (
                <tr key={f} className="border-t border-muted/40">
                  <td className="py-1 pr-2 font-medium capitalize text-muted-foreground">
                    {f.replace(/_/g, ' ')}
                  </td>
                  <td className="py-1 pr-2 text-muted-foreground">{vals.old || <em className="opacity-60">(empty)</em>}</td>
                  <td className="py-1 pr-2 text-foreground font-medium">{vals.new || <em className="opacity-60">(empty)</em>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </label>
    </div>
  );
}
